"use client";

import NotificationList from "../components/NotificationList";
import { mockNotifications } from "../mocks/notifications.mock";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Home,
  Info,
  MessageSquare,
  X,
} from "lucide-react";
import React, { useState } from "react";

const ITEMS_PER_PAGE = 10;

const NotificationsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const filteredNotifications = mockNotifications.filter((notification) => {
    if (selectedType && notification.type !== selectedType) return false;
    if (selectedModule && notification.module !== selectedModule) return false;
    if (showUnreadOnly && notification.isRead) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedNotifications = filteredNotifications.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleMarkAsRead = (notification: typeof mockNotifications[0]) => {
    // Тут буде логіка позначення сповіщення як прочитаного
    console.log("Marking as read:", notification);
  };

  const handleDelete = (notification: typeof mockNotifications[0]) => {
    // Тут буде логіка видалення сповіщення
    console.log("Deleting:", notification);
  };

  return (
    <div className="container mx-auto p-8 space-y-8">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-primary/10 rounded-full">
          <Bell className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Сповіщення</h1>
          <p className="text-muted-foreground">
            Перегляд та управління сповіщеннями системи
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <Button
          variant={showUnreadOnly ? "default" : "outline"}
          onClick={() => setShowUnreadOnly(!showUnreadOnly)}
        >
          Непрочитані
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex gap-2">
          <Button
            variant={selectedType === "info" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedType(selectedType === "info" ? null : "info")}
          >
            <Info className="h-4 w-4 mr-2" />
            Інформація
          </Button>
          <Button
            variant={selectedType === "warning" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedType(selectedType === "warning" ? null : "warning")
            }
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Попередження
          </Button>
          <Button
            variant={selectedType === "success" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedType(selectedType === "success" ? null : "success")
            }
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Успішно
          </Button>
          <Button
            variant={selectedType === "error" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedType(selectedType === "error" ? null : "error")
            }
          >
            <X className="h-4 w-4 mr-2" />
            Помилки
          </Button>
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex gap-2">
          <Button
            variant={selectedModule === "documents" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedModule(selectedModule === "documents" ? null : "documents")
            }
          >
            <FileText className="h-4 w-4 mr-2" />
            Документи
          </Button>
          <Button
            variant={selectedModule === "properties" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedModule(
                selectedModule === "properties" ? null : "properties"
              )
            }
          >
            <Home className="h-4 w-4 mr-2" />
            Нерухомість
          </Button>
          <Button
            variant={selectedModule === "payments" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedModule(selectedModule === "payments" ? null : "payments")
            }
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Платежі
          </Button>
          <Button
            variant={selectedModule === "requests" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setSelectedModule(selectedModule === "requests" ? null : "requests")
            }
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Запити
          </Button>
        </div>
      </div>

      <div className="bg-background rounded-lg border shadow-sm">
        <NotificationList
          notifications={paginatedNotifications}
          onMarkAsRead={handleMarkAsRead}
          onDelete={handleDelete}
        />

        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Показано {startIndex + 1}-
              {Math.min(startIndex + ITEMS_PER_PAGE, filteredNotifications.length)}{" "}
              з {filteredNotifications.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="icon"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage; 